
import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    ArcElement
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import styles from './styles/SalesAnalytics.module.css';
import shared from './styles/adminShared.module.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

interface SalesData {
    metodo?: string;
    Producto?: string;
    categoria?: string;
    semana: number;
    total: number;
    cantidad?: number;
    cantidad_individual?: number;
    cantidad_promo?: number;
}

interface Local {
    locales_id: number;
    nombre_local: string;
}

interface PeriodMetrics {
    total: number;
    pedidos: number;
    ticket_promedio: number;
    dias: number[];
}

interface LocalWeeklyComparison {
    id_local: number;
    nombre_local: string;
    current: PeriodMetrics;
    previous: PeriodMetrics;
    delta_total_pct: number | null;
    delta_pedidos_pct: number | null;
}

interface LocalWeeklyResponse {
    current_week: { start: string; end: string };
    previous_week: { start: string; end: string };
    day_labels: string[];
    totals: {
        current: PeriodMetrics;
        previous: PeriodMetrics;
        delta_total_pct: number | null;
        delta_pedidos_pct: number | null;
    };
    locales: LocalWeeklyComparison[];
}

const getMonday = (date = new Date()) => {
    const copy = new Date(date);
    const day = copy.getDay();
    copy.setDate(copy.getDate() + (day === 0 ? -6 : 1 - day));
    return copy;
};

const toInputDate = (date: Date) => date.toISOString().slice(0, 10);
const addDays = (value: string, days: number) => {
    const date = new Date(`${value}T00:00:00`);
    date.setDate(date.getDate() + days);
    return toInputDate(date);
};
const formatShortDate = (value: string) =>
    new Date(`${value}T00:00:00`).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
const formatMoney = (value: number) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(value || 0);
const formatDelta = (value: number | null) => {
    if (value === null) return 'Nuevo';
    return `${value > 0 ? '+' : ''}${value}%`;
};

const getLocalComparisonChartData = (comparison: LocalWeeklyResponse) => ({
    labels: comparison.locales.map(local => local.nombre_local),
    datasets: [
        {
            label: 'Semana actual',
            data: comparison.locales.map(local => local.current.total),
            backgroundColor: 'rgba(243, 156, 18, 0.85)',
            borderColor: 'rgba(243, 156, 18, 1)',
            borderWidth: 1
        },
        {
            label: 'Semana anterior',
            data: comparison.locales.map(local => local.previous.total),
            backgroundColor: 'rgba(255, 255, 255, 0.45)',
            borderColor: 'rgba(255, 255, 255, 0.9)',
            borderWidth: 1
        }
    ]
});

const localComparisonChartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'bottom' as const,
            labels: { color: '#f8f9fa' }
        },
        title: {
            display: true,
            text: 'Ventas por local: semana actual vs anterior',
            color: '#f8f9fa'
        },
        tooltip: {
            callbacks: {
                label: (context: any) => `${context.dataset.label}: ${formatMoney(Number(context.raw || 0))}`
            }
        }
    },
    scales: {
        x: {
            ticks: {
                color: '#f8f9fa',
                callback: (value: string | number) => formatMoney(Number(value))
            },
            grid: { color: 'rgba(255, 255, 255, 0.12)' }
        },
        y: {
            ticks: { color: '#f8f9fa' },
            grid: { color: 'rgba(255, 255, 255, 0.08)' }
        }
    }
};

const SalesAnalytics: React.FC = () => {
    // Controls
    const [view, setView] = useState<'products' | 'shipping' | 'payment' | 'locals'>('products');
    const [weekStart, setWeekStart] = useState(() => toInputDate(getMonday()));
    const [localComparison, setLocalComparison] = useState<LocalWeeklyResponse | null>(null);

    // Initialize with current month
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });

    const [selectedLocal, setSelectedLocal] = useState<string>(''); // '' = Global
    const [locales, setLocales] = useState<Local[]>([]);

    // Product filters
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedProduct, setSelectedProduct] = useState<string>('');
    const [productSearch, setProductSearch] = useState<string>('');
    const [maxProducts, setMaxProducts] = useState<number>(10);

    // Data
    const [data, setData] = useState<SalesData[]>([]);
    const [loading, setLoading] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        // Fetch locales
        fetch(`${API_URL}/locales`)
            .then(res => res.json())
            .then(res => {
                if (Array.isArray(res)) setLocales(res);
            })
            .catch(console.error);
    }, []);

    useEffect(() => {
        setLoading(true);
        if (view === 'locals') {
            fetch(`${API_URL}/admin/stats/local-weekly-comparison?week_start=${weekStart}`)
                .then(res => res.json())
                .then(res => {
                    setLocalComparison(res);
                    setData([]);
                    setLoading(false);
                })
                .catch(err => {
                    console.error(err);
                    setLocalComparison(null);
                    setData([]);
                    setLoading(false);
                });
            return;
        }

        let endpoint = '';
        // Build query params
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (selectedLocal) params.append('id_local', selectedLocal);

        if (view === 'products') {
            endpoint = `${API_URL}/admin/stats/weekly-products`;
        } else if (view === 'shipping') {
            endpoint = `${API_URL}/admin/stats/sales-by-method?type=shipping`;
        } else if (view === 'payment') {
            endpoint = `${API_URL}/admin/stats/sales-by-method?type=payment`;
        }

        // Note: endpoint might already have ?type=... so apply params carefully
        const url = new URL(endpoint);
        params.forEach((v, k) => url.searchParams.append(k, v));

        fetch(url.toString())
            .then(res => res.json())
            .then(res => {
                console.log("Stats Data:", res);
                setData(Array.isArray(res) ? res : []);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setData([]);
                setLoading(false);
            });

    }, [view, startDate, endDate, selectedLocal, weekStart, API_URL]);

    // --- Derived lists for filters ---
    const allCategories = view === 'products'
        ? Array.from(new Set(data.map(d => d.categoria).filter(Boolean))).sort() as string[]
        : [];

    const allProducts = view === 'products'
        ? Array.from(new Set(
            data
                .filter(d => !selectedCategory || d.categoria === selectedCategory)
                .map(d => d.Producto)
                .filter(Boolean)
          )).sort() as string[]
        : [];

    // Group data by entity (Product or Method) to remove weekly division
    const aggregatedData = React.useMemo(() => {
        const groups: Record<string, SalesData> = {};
        
        data.forEach(d => {
            const key = view === 'products' ? d.Producto || 'Desconocido' : d.metodo || 'Desconocido';
            if (!groups[key]) {
                groups[key] = {
                    ...d,
                    semana: 0, // Not used in aggregated view
                    total: 0,
                    cantidad: 0,
                    cantidad_individual: 0,
                    cantidad_promo: 0
                };
            }
            groups[key].total += Number(d.total || 0);
            groups[key].cantidad = (groups[key].cantidad || 0) + Number(d.cantidad || 0);
            groups[key].cantidad_individual = (groups[key].cantidad_individual || 0) + Number(d.cantidad_individual || 0);
            groups[key].cantidad_promo = (groups[key].cantidad_promo || 0) + Number(d.cantidad_promo || 0);
        });

        return Object.values(groups).sort((a, b) => b.total - a.total);
    }, [data, view]);

    // Filtered data for aggregated view
    const filteredData = aggregatedData.filter(d => {
        if (view === 'products') {
            if (selectedCategory && d.categoria !== selectedCategory) return false;
            if (selectedProduct && d.Producto !== selectedProduct) return false;
            if (productSearch && !d.Producto?.toLowerCase().includes(productSearch.toLowerCase())) return false;
        }
        return true;
    });

    // --- Chart Prep ---
    const getChartData = () => {
        let entities = filteredData.map(d => view === 'products' ? d.Producto : d.metodo).filter(Boolean) as string[];

        // Limit to top N unless searching/selecting
        if (!selectedProduct && !productSearch) {
            entities = entities.slice(0, maxProducts);
        }

        const colors = [
            '#3498db', '#e74c3c', '#2ecc71', '#f1c40f', '#9b59b6', '#34495e',
            '#1abc9c', '#e67e22', '#95a5a6', '#d35400', '#7f8c8d', '#8e44ad'
        ];

        return {
            labels: entities,
            datasets: [
                {
                    label: view === 'products' ? 'Cantidades Vendidas' : 'Ingresos ($)',
                    data: entities.map(e => {
                        const row = filteredData.find(d => (view === 'products' ? d.Producto === e : d.metodo === e));
                        return view === 'products' ? Number(row?.cantidad || 0) : Number(row?.total || 0);
                    }),
                    backgroundColor: colors.slice(0, entities.length),
                    borderWidth: 1
                }
            ]
        };
    };

    const getTotals = () => {
        const totalAmount = filteredData.reduce((acc: number, curr: SalesData) => acc + Number(curr.total || 0), 0);
        const totalQty = filteredData.reduce((acc: number, curr: SalesData) => acc + Number(curr.cantidad || 0), 0);
        return { totalAmount, totalQty };
    };

    const totals = getTotals();

    return (
        <div className={styles['analytics-container']}>
            <h3>Datos de Ventas y Analíticas</h3>

            <div className={styles['controls']}>
                <div className={styles['control-group']}>
                    <label>Vista de Datos</label>
                    <select value={view} onChange={e => setView(e.target.value as any)} className={styles['select-input']}>
                        <option value="products">Ingresos y Cantidades por Producto</option>
                        <option value="shipping">Ingresos por Método de Envío</option>
                        <option value="payment">Ingresos por Método de Pago</option>
                        <option value="locals">Comparativa semanal por local</option>
                    </select>
                </div>

                {view !== 'locals' && <div className={styles['control-group']}>
                    <label>Local</label>
                    <select value={selectedLocal} onChange={e => setSelectedLocal(e.target.value)} className={styles['select-input']}>
                        <option value="">GLOBAL (Todos)</option>
                        {locales.map(l => (
                            <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                        ))}
                    </select>
                </div>}

                {view === 'locals' && (
                    <div className={styles['control-group']}>
                        <label>Semana lunes-domingo</label>
                        <div className={styles['week-controls']}>
                            <button type="button" onClick={() => setWeekStart(current => addDays(current, -7))}>&lt;</button>
                            <input
                                type="date"
                                value={weekStart}
                                onChange={e => setWeekStart(toInputDate(getMonday(new Date(`${e.target.value}T00:00:00`))))}
                                className={styles['select-input']}
                            />
                            <button type="button" onClick={() => setWeekStart(current => addDays(current, 7))}>&gt;</button>
                        </div>
                    </div>
                )}

                {view !== 'locals' && <div className={styles['control-group']}>
                    <label>Desde</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        className={styles['select-input']}
                    />
                </div>}
                {view !== 'locals' && <div className={styles['control-group']}>
                    <label>Hasta</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        className={styles['select-input']}
                    />
                </div>}

                {view === 'products' && (
                    <>
                        <div className={styles['control-group']}>
                            <label>Categoría</label>
                            <select
                                value={selectedCategory}
                                onChange={e => { setSelectedCategory(e.target.value); setSelectedProduct(''); }}
                                className={styles['select-input']}
                            >
                                <option value="">Todas</option>
                                {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className={styles['control-group']}>
                            <label>Buscar Producto</label>
                            <input 
                                type="text"
                                placeholder="Escribe para buscar..."
                                value={productSearch}
                                onChange={e => {
                                    setProductSearch(e.target.value);
                                    setSelectedProduct('');
                                }}
                                className={styles['select-input']}
                            />
                        </div>
                        <div className={styles['control-group']}>
                            <label>O Seleccionar</label>
                            <select
                                value={selectedProduct}
                                onChange={e => {
                                    setSelectedProduct(e.target.value);
                                    setProductSearch('');
                                }}
                                className={styles['select-input']}
                            >
                                <option value="">Top {maxProducts}</option>
                                {allProducts.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        {!selectedProduct && !productSearch && (
                            <div className={styles['control-group']}>
                                <label>Top N</label>
                                <select
                                    value={maxProducts}
                                    onChange={e => setMaxProducts(Number(e.target.value))}
                                    className={styles['select-input']}
                                >
                                    {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                        )}
                    </>
                )}
            </div>

            {view === 'locals' && localComparison && (
                <>
                    <div className={styles['local-week-header']}>
                        <span>
                            Semana actual {formatShortDate(localComparison.current_week.start)} - {formatShortDate(localComparison.current_week.end)}
                            {' '}vs anterior {formatShortDate(localComparison.previous_week.start)} - {formatShortDate(localComparison.previous_week.end)}
                        </span>
                    </div>

                    <div className={styles['summary-cards']}>
                        <div className={styles['card']}>
                            <h4>Ventas semana actual</h4>
                            <div className={styles['value']}>{formatMoney(localComparison.totals.current.total)}</div>
                            <small className={localComparison.totals.delta_total_pct !== null && localComparison.totals.delta_total_pct < 0 ? styles.down : styles.up}>
                                {formatDelta(localComparison.totals.delta_total_pct)} vs semana anterior
                            </small>
                        </div>
                        <div className={styles['card']}>
                            <h4>Pedidos</h4>
                            <div className={styles['value']}>{localComparison.totals.current.pedidos}</div>
                            <small className={localComparison.totals.delta_pedidos_pct !== null && localComparison.totals.delta_pedidos_pct < 0 ? styles.down : styles.up}>
                                {formatDelta(localComparison.totals.delta_pedidos_pct)} vs semana anterior
                            </small>
                        </div>
                        <div className={styles['card']}>
                            <h4>Ticket promedio</h4>
                            <div className={styles['value']}>{formatMoney(localComparison.totals.current.ticket_promedio)}</div>
                            <small>Base lunes a domingo</small>
                        </div>
                    </div>

                    <div className={`${styles['chart-section']} ${styles['local-comparison-chart']}`}>
                        <Bar
                            data={getLocalComparisonChartData(localComparison)}
                            options={localComparisonChartOptions}
                        />
                    </div>

                    <div className={styles['data-section']}>
                        <h4>Comparativa por local</h4>
                        <table className={styles['data-table']}>
                            <thead>
                                <tr>
                                    <th>Local</th>
                                    <th>Ventas actual</th>
                                    <th>Ventas anterior</th>
                                    <th>Var.</th>
                                    <th>Pedidos actual/anterior</th>
                                    <th>Ticket prom.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {localComparison.locales.map(local => (
                                    <tr key={local.id_local || local.nombre_local}>
                                        <td>{local.nombre_local}</td>
                                        <td>{formatMoney(local.current.total)}</td>
                                        <td>{formatMoney(local.previous.total)}</td>
                                        <td className={local.delta_total_pct !== null && local.delta_total_pct < 0 ? styles.down : styles.up}>
                                            {formatDelta(local.delta_total_pct)}
                                        </td>
                                        <td>{local.current.pedidos} / {local.previous.pedidos}</td>
                                        <td>{formatMoney(local.current.ticket_promedio)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {view !== 'locals' && (
            <>
            <div className={styles['summary-cards']}>
                <div className={styles['card']}>
                    <h4>Total Ingresos (Vista Actual)</h4>
                    <div className={styles['value']}>${totals.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                {view === 'products' && (
                    <div className={styles['card']}>
                        <h4>Total Unidades Vendidas</h4>
                        <div className={styles['value']}>{totals.totalQty}</div>
                    </div>
                )}
            </div>

            <div className={`${styles['chart-section']} ${shared.chartRegion}`}>
                {loading ? <p className={shared.loadingState}>Cargando gráfico...</p> : (
                    data.length > 0 ? (
                        <Bar
                            data={getChartData()}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'bottom' },
                                    title: {
                                        display: true,
                                        text: view === 'products' ? 'Unidades Vendidas por Producto' : 'Ingresos por Categoría'
                                    }
                                }
                            }}
                        />
                    ) : (
                        <p>No hay datos para los filtros seleccionados.</p>
                    )
                )}
            </div>

            <div className={styles['data-section']}>
                <h4>Detalle de Datos</h4>
                <table className={styles['data-table']}>
                    <thead>
                        <tr>
                            <th>{view === 'products' ? 'Producto' : 'Método'}</th>
                            {view === 'products' && <th>Categoría</th>}
                            {view === 'products' && (
                                <>
                                    <th>Cant. Indiv.</th>
                                    <th>Cant. x Promos</th>
                                    <th>Total Unidades</th>
                                </>
                            )}
                            <th>Ingresos ($)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((row, idx) => (
                            <tr key={idx}>
                                <td>{view === 'products' ? row.Producto : row.metodo}</td>
                                {view === 'products' && <td>{row.categoria || '—'}</td>}
                                {view === 'products' && (
                                    <>
                                        <td>{row.cantidad_individual || 0}</td>
                                        <td>{row.cantidad_promo || 0}</td>
                                        <td style={{ fontWeight: 'bold' }}>{row.cantidad}</td>
                                    </>
                                )}
                                <td>${(row.total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </>
            )}
        </div>
    );
};

export default SalesAnalytics;
